import Link from 'next/link'
import {useRouter} from 'next/router'
import {createElement, memo, useCallback, useMemo} from 'react'

import {IconArrowDownRoundBold, IconBackButtonBoldSemi} from '@defign/asset'
import {IcAddCircleStroke, IcTopPrev} from '@defign/ui'
import {useUserAgentContext} from '@financial/common-contexts'
import {Action, sendAULog} from '@financial/common-logs'
import {CONNECT_SCHEME} from '@mydata/utils/connect'
import classNames from 'classnames/bind'

import ExpireNotificationTooltip from '$components/common/ExpireNotificationTooltip'
import {AssetConnectStatus} from '$constants/connect'
import {EXTERNAL_ROUTES} from '$constants/external-routes'
import {useConnectQueryCheckBack} from '$hooks/useConnectQueryCheckBack'
import {useNewConnectionUrl} from '$hooks/useConnectUrl'
import baseOrigin from '$utils/baseOrigin'
import buildUrl from '$utils/buildUrl'
import {displaySpace, goToPayAppMyAsset} from '$utils/common'
import {sendClickNlog} from '$utils/nlog'

import Styles from './header.module.scss'
import StaticFixedArea from './static-fixed-area'

import type {AUParam} from '@financial/common-logs'
import type {ValueOf} from 'next/constants'
import type {MouseEvent, MouseEventHandler, ReactNode} from 'react'
import type {UrlObject} from 'url'

interface MyAssetHeaderProps {
    title: ReactNode
    children: ReactNode
    isFixed?: boolean
    showShadowLine?: boolean
    typeButton?: boolean
    blindText?: boolean
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void
    forceBackgroundWhite?: boolean
}

const cx = classNames.bind(Styles)

export const Header = memo(function Header({
    title,
    children,
    typeButton,
    blindText,
    onClick: handleClick,
    forceBackgroundWhite = false,
}: MyAssetHeaderProps) {
    const titleSection = useMemo(() => {
        if (typeof title === 'string') {
            return typeButton ? (
                <button type="button" className={cx('button-select')} onClick={handleClick}>
                    {displaySpace(title)}
                    <IconArrowDownRoundBold width={11} height={8} fill="#1e1e23" />
                </button>
            ) : blindText ? (
                <span className="blind">{title}</span>
            ) : (
                <>{displaySpace(title)}</>
            )
        }

        return title
    }, [blindText, handleClick, title, typeButton])
    return (
        <div id="header" className={cx('article', {'type-white': forceBackgroundWhite})}>
            <h1 className="blind">네이버페이 내자산</h1>
            <h2 className={cx('title')}>{titleSection}</h2>
            {children}
        </div>
    )
})

export const MyAssetHeader = ({
    title,
    children,
    isFixed = true,
    showShadowLine = true,
    typeButton = false,
    blindText = false,
}: MyAssetHeaderProps) => {
    if (isFixed) {
        return (
            <StaticFixedArea showShadowLine={showShadowLine}>
                <Header blindText={blindText} title={title} typeButton={typeButton}>
                    {children}
                </Header>
            </StaticFixedArea>
        )
    }

    return (
        <Header title={title} typeButton={typeButton}>
            {children}
        </Header>
    )
}

interface CommonLeftBackButtonProps {
    onClick: MouseEventHandler
}

export const CommonLeftBackButton = ({onClick}: CommonLeftBackButtonProps) => {
    return (
        <div className={cx('area-left')}>
            <button className={cx('button', 'prev-arrow')} onClick={onClick}>
                <IconBackButtonBoldSemi width={20} height={18} />
                <span className="blind">이전으로 이동</span>
            </button>
        </div>
    )
}

interface ButtonProps {
    nClickCode: string
    href?: UrlObject | string
    text?: string
    onClick?: MouseEventHandler<HTMLAnchorElement>
}

export const LeftMyAssetButton = memo(({nClickCode, text = '내 자산', href, onClick}: ButtonProps) => {
    const {isPayApp} = useUserAgentContext()
    const 페이앱_내자산_뒤로가기 = isPayApp && text === '내 자산'
    const link = 페이앱_내자산_뒤로가기 ? '#' : href || `${baseOrigin}${EXTERNAL_ROUTES.MYDATA.HOME}`

    const handleClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
        if (페이앱_내자산_뒤로가기 && typeof window.payInAppBrowser?.showServiceMenu === 'function') {
            goToPayAppMyAsset()
        }

        sendClickNlog(nClickCode)
        onClick?.(event)
    }

    return (
        <div className={cx('area-left')}>
            {createElement(
                페이앱_내자산_뒤로가기 ? 'a' : Link,
                {href: link, className: cx('button', 'prev'), onClick: handleClick},
                <>
                    <IcTopPrev width={10} height={18} fill="#1e1e23" />
                    {text}
                    <span className="blind">으로 이동</span>
                </>,
            )}
        </div>
    )
})

interface RightRegistrationButtonProps extends ButtonProps {
    text: string
    auCode?: string
    auParam?: AUParam
    scheme?: ValueOf<typeof CONNECT_SCHEME> | 'mydata_bank0'
}

export const RightRegistrationButton = ({
    text,
    auCode,
    auParam,
    nClickCode,
    scheme = CONNECT_SCHEME.MYDATA_BANK,
}: RightRegistrationButtonProps) => {
    const {enterConnectUrl} = useNewConnectionUrl({scheme})

    const handleClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
        event.preventDefault()
        sendClickNlog(nClickCode)
        if (auCode || auParam) {
            const auLog = auParam || (auCode ? {service: 'md', area: 'B', page: 'B', code: auCode} : undefined)
            auLog && sendAULog(auLog, Action.Click)
        }

        enterConnectUrl()
    }

    return (
        <div className={cx('area-right')}>
            <a className={cx('button', 'connect')} onClick={handleClick}>
                <IcAddCircleStroke width={18} height={18} fill="#09aa5c" />
                {text} 연결
            </a>
        </div>
    )
}

interface LeftLinkButtonProps extends ButtonProps {
    text: string
    href?: string | UrlObject
    onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
}

export const LeftLinkButton = memo(function MemoizedLeftLink({text, href, nClickCode, onClick}: LeftLinkButtonProps) {
    const handleClick = useCallback(
        (event: MouseEvent<HTMLAnchorElement>) => {
            sendClickNlog(nClickCode)
            onClick?.(event)
        },
        [nClickCode, onClick],
    )

    return (
        <div className={cx('area-left')}>
            {href && (
                <Link href={href} className={cx('button', 'prev')} onClick={handleClick}>
                    <IcTopPrev width={10} height={18} fill="#1e1e23" />
                    {text}
                    <span className="blind">으로 이동</span>
                </Link>
            )}
            {onClick && (
                <a className={cx('button', 'prev')} onClick={handleClick}>
                    <IcTopPrev width={10} height={18} fill="#1e1e23" />
                    {text}
                    <span className="blind">으로 이동</span>
                </a>
            )}
        </div>
    )
})

interface RightSettingButtonProps extends ButtonProps {
    expDate?: string | null
    isConnectedToMyAsset?: boolean
    id?: number | string
    moveToCorp?: boolean
    orgCode?: string
}

export const RightSettingButton = ({
    expDate,
    nClickCode,
    id,
    isConnectedToMyAsset = true,
    moveToCorp = false,
    orgCode,
}: RightSettingButtonProps) => {
    const url = useMemo(() => {
        if (moveToCorp && orgCode) {
            return buildUrl({
                pathname: EXTERNAL_ROUTES.MYDATA.MANAGEMENT.CORP,
                query: {
                    orgCode,
                },
            })
        }

        return buildUrl({
            pathname: EXTERNAL_ROUTES.MYDATA.MANAGEMENT.ASSET,
            query: {
                status: isConnectedToMyAsset ? AssetConnectStatus.Connected : AssetConnectStatus.Disconnected,
                id,
            },
        })
    }, [id, isConnectedToMyAsset, moveToCorp, orgCode])

    const handleClick: MouseEventHandler = () => {
        sendClickNlog(nClickCode)
    }

    return (
        <div className={cx('area-right')}>
            <Link href={url} className={cx('button', 'setting')} onClick={handleClick}>
                관리
            </Link>

            {expDate && <ExpireNotificationTooltip expDate={expDate} />}
        </div>
    )
}

export const LeftBackButton = memo(({nClickCode, close = false}: ButtonProps & {close?: boolean}) => {
    const {isNaverApp, isPayApp} = useUserAgentContext()
    const router = useRouter()

    const handleClick: MouseEventHandler = useCallback(() => {
        sendClickNlog(nClickCode)

        if (close) {
            if (isNaverApp || isPayApp) {
                window.location.href = 'naverapp://closewindow'
            } else {
                window.close()
            }
        } else {
            router.back()
        }
    }, [close, isNaverApp, isPayApp, nClickCode, router])

    return <CommonLeftBackButton onClick={handleClick} />
})

export const LeftConnectQueryCheckBackButton = ({nClickCode}: {nClickCode?: string}) => {
    const {handleClick} = useConnectQueryCheckBack(nClickCode)

    return <CommonLeftBackButton onClick={handleClick} />
}
